/*
  Ever Leaf Gallery — Mobile Carousel + shared Lightbox, isolated in its own
  file/custom element (not the shared ever-leaf.js IIFE) so its lifecycle
  never couples to Testimonials or any other component.

  Two performance/architecture guarantees this file is built around:

  1. The Lightbox's full ordered image list comes from a tiny inert JSON
     <script type="application/json"> data island (see
     sections/ever-leaf-gallery.liquid), never from querying the Mobile
     Carousel's DOM. Desktop-only sessions still get correct Lightbox
     navigation ("3 / 12") across the entire Gallery, independent of
     whether the Mobile Carousel has ever been instantiated.

  2. The Mobile Carousel's actual <img> tiles live inside an inert
     <template> until this file clones them into the live DOM — and only
     does so the first time the viewport is actually at Mobile width
     (window.matchMedia, not a raw resize listener). A Desktop visitor who
     never resizes below 990px never causes the browser to fetch a single
     Mobile thumbnail; <template> content is spec-guaranteed inert, unlike
     relying on CSS display:none against a real <img src>.

  Otherwise reuses the same lessons already proven on Testimonials: native
  scroll-snap, IntersectionObserver with the track as root, element-based
  scrollIntoView navigation (never raw scrollLeft math), AbortController for
  listener cleanup, and a guarded disconnectedCallback so Shopify Theme
  Editor reloads never accumulate duplicate listeners. Multiple Gallery
  sections on one page do not share any state — every property below is
  per-instance.
*/
(function () {
  'use strict';

  if (customElements.get('ever-leaf-gallery-app')) return;

  var DESKTOP_QUERY = '(min-width: 990px)';

  class EverLeafGalleryApp extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.galleryData = this.readGalleryData();
      this.totalCount = this.galleryData.length;

      this.desktopTriggers = Array.from(this.querySelectorAll('.ever-leaf-gallery__collage [data-gallery-index]'));
      this.mobileTriggers = [];

      this.mobileTemplate = this.querySelector('[data-gallery-mobile-template]');
      this.mobileTrack = this.querySelector('[data-gallery-mobile-track]');
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.dots = Array.from(this.querySelectorAll('.ever-leaf-gallery__dot'));

      this.lightbox = this.querySelector('.ever-leaf-gallery__lightbox');
      this.lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
      this.lightboxCounter = this.querySelector('[data-gallery-lightbox-counter]');
      this.lightboxClose = this.querySelector('[data-gallery-lightbox-close]');
      this.lightboxPrev = this.querySelector('[data-gallery-lightbox-prev]');
      this.lightboxNext = this.querySelector('[data-gallery-lightbox-next]');
      this.lightboxBackdrop = this.querySelector('[data-gallery-lightbox-backdrop]');
      this.lightboxMedia = this.lightbox ? this.lightbox.querySelector('.ever-leaf-gallery__lightbox-media') : null;

      this.slides = [];
      this.mobileInstantiated = false;
      this.currentSlide = 0;
      this.lightboxIndex = 0;
      this.lastFocused = null;
      this.touchStartX = null;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.desktopTriggers.forEach((trigger) => this.wireTrigger(trigger, signal));

      if (this.lightbox) {
        if (this.lightboxClose) this.lightboxClose.addEventListener('click', () => this.closeLightbox(), { signal });
        if (this.lightboxBackdrop) this.lightboxBackdrop.addEventListener('click', () => this.closeLightbox(), { signal });
        if (this.lightboxPrev) this.lightboxPrev.addEventListener('click', () => this.showLightboxIndex(this.lightboxIndex - 1), { signal });
        if (this.lightboxNext) this.lightboxNext.addEventListener('click', () => this.showLightboxIndex(this.lightboxIndex + 1), { signal });

        this.lightbox.addEventListener(
          'keydown',
          (event) => {
            const isRtl = document.dir === 'rtl';
            if (event.key === 'Escape') {
              this.closeLightbox();
            } else if (event.key === 'ArrowLeft') {
              /* Deliberately RTL-aware / reading-direction-relative, unlike
                 the Prev/Next buttons' own fixed physical position and icon
                 rotation (left ever-leaf-gallery__arrow--prev, always
                 pointing left; right ever-leaf-gallery__arrow--next, always
                 pointing right, regardless of dir — see the CSS, untouched
                 here). Keyboard semantics and visual button layout are
                 intentionally decoupled per explicit instruction. */
              isRtl ? this.showLightboxIndex(this.lightboxIndex + 1) : this.showLightboxIndex(this.lightboxIndex - 1);
            } else if (event.key === 'ArrowRight') {
              isRtl ? this.showLightboxIndex(this.lightboxIndex - 1) : this.showLightboxIndex(this.lightboxIndex + 1);
            } else if (event.key === 'Tab') {
              this.trapFocus(event);
            }
          },
          { signal }
        );

        if (this.lightboxMedia) {
          this.lightboxMedia.addEventListener(
            'touchstart',
            (event) => {
              this.touchStartX = event.touches[0].clientX;
            },
            { signal, passive: true }
          );
          this.lightboxMedia.addEventListener(
            'touchend',
            (event) => {
              if (this.touchStartX === null) return;
              const deltaX = event.changedTouches[0].clientX - this.touchStartX;
              this.touchStartX = null;
              if (Math.abs(deltaX) < 40) return;
              const isRtl = document.dir === 'rtl';
              const swipedForward = deltaX < 0;
              if (swipedForward) {
                isRtl ? this.showLightboxIndex(this.lightboxIndex - 1) : this.showLightboxIndex(this.lightboxIndex + 1);
              } else {
                isRtl ? this.showLightboxIndex(this.lightboxIndex + 1) : this.showLightboxIndex(this.lightboxIndex - 1);
              }
            },
            { signal }
          );
        }
      }

      /* matchMedia, not a raw resize listener — fires only on an actual
         breakpoint crossing, not on every pixel of a drag-resize. Instantiation
         is one-time and irreversible per element instance: once Mobile has
         been shown, its DOM/listeners stay alive (harmless) rather than being
         torn down and rebuilt on every crossing, which is what "do not lose
         carousel state catastrophically" (per the brief) is guarding against. */
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.handleBreakpointChange = () => {
        if (!this.desktopQuery.matches) this.ensureMobileCarousel();
      };
      this.desktopQuery.addEventListener('change', this.handleBreakpointChange, { signal });
      if (!this.desktopQuery.matches) this.ensureMobileCarousel();
    }

    readGalleryData() {
      const node = this.querySelector('[data-gallery-data]');
      if (!node) return [];
      try {
        const parsed = JSON.parse(node.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    wireTrigger(trigger, signal) {
      trigger.addEventListener(
        'click',
        () => {
          const index = parseInt(trigger.getAttribute('data-gallery-index'), 10);
          this.openLightbox(index, trigger);
        },
        { signal }
      );
    }

    ensureMobileCarousel() {
      if (this.mobileInstantiated) return;
      if (!this.mobileTemplate || !this.mobileTrack) return;
      this.mobileInstantiated = true;

      this.mobileTrack.appendChild(this.mobileTemplate.content.cloneNode(true));
      this.slides = Array.from(this.mobileTrack.querySelectorAll('.ever-leaf-gallery__mobile-slide'));
      this.mobileTriggers = Array.from(this.mobileTrack.querySelectorAll('[data-gallery-index]'));

      const { signal } = this.abortController;
      this.mobileTriggers.forEach((trigger) => this.wireTrigger(trigger, signal));

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goToSlide(this.currentSlide - 1), { signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goToSlide(this.currentSlide + 1), { signal });
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index), { signal });
      });

      if ('IntersectionObserver' in window && this.slides.length) {
        this.slideObserver = new IntersectionObserver(
          (entries) => {
            let best = null;
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
              }
            });
            if (best) {
              const index = this.slides.indexOf(best.target);
              if (index !== -1) this.setActiveSlide(index);
            }
          },
          { root: this.mobileTrack, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.slideObserver.observe(slide));
      }

      this.setActiveSlide(0);
    }

    goToSlide(index) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      if (this.slides[clamped]) this.slides[clamped].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    setActiveSlide(index) {
      this.currentSlide = index;
      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = index === 0;
      if (this.nextBtn) this.nextBtn.disabled = index === this.slides.length - 1;
    }

    openLightbox(index, triggerEl) {
      if (!this.lightbox) return;
      this.lastFocused = triggerEl || document.activeElement;
      this.showLightboxIndex(index);
      this.lightbox.hidden = false;
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ever-leaf-gallery-lightbox-open');
      if (this.lightboxClose) this.lightboxClose.focus();
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.hidden = true;
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ever-leaf-gallery-lightbox-open');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
    }

    showLightboxIndex(index) {
      if (this.totalCount === 0) return;
      const clamped = Math.max(0, Math.min(index, this.totalCount - 1));
      this.lightboxIndex = clamped;

      const entry = this.galleryData[clamped];
      if (entry && this.lightboxImage) {
        this.lightboxImage.src = entry.src || '';
        this.lightboxImage.alt = entry.alt || '';
      }
      if (this.lightboxCounter) {
        this.lightboxCounter.textContent = clamped + 1 + ' / ' + this.totalCount;
      }
      if (this.lightboxPrev) this.lightboxPrev.disabled = clamped === 0;
      if (this.lightboxNext) this.lightboxNext.disabled = clamped === this.totalCount - 1;
    }

    trapFocus(event) {
      if (!this.lightbox) return;
      const focusable = Array.from(this.lightbox.querySelectorAll('button:not([disabled])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.slideObserver) {
        this.slideObserver.disconnect();
        this.slideObserver = null;
      }
      document.body.classList.remove('ever-leaf-gallery-lightbox-open');
      this.initialized = false;
      this.mobileInstantiated = false;
    }
  }

  customElements.define('ever-leaf-gallery-app', EverLeafGalleryApp);
})();
